import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SportsEsportsRoundedIcon from "@mui/icons-material/SportsEsportsRounded";
import { AppBar, Avatar, Box, IconButton, Toolbar, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/** Toolbar row height; keep `App` content `paddingTop` and full-viewport layouts in sync. */
export const SESSION_APPBAR_TOOLBAR_PX = { xs: 54, sm: 58 } as const;

type Props = {
  displayName: string;
  onOpenProfile: () => void;
  /** Addata ranked leaderboard (optional). */
  onOpenLeaderboard?: () => void;
  /** Extra right padding on the toolbar (e.g. fixed game menu toggle sits in this gutter). */
  toolbarTrailingReservePx?: number;
};

export const SessionAppBar = ({
  displayName,
  onOpenProfile,
  onOpenLeaderboard,
  toolbarTrailingReservePx = 0,
}: Props) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("sm"));
  const initial = displayName.trim().charAt(0).toUpperCase() || "?";

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        /* Above in-app surfaces (Paper, portaled widgets); stay below modals (1300+). */
        zIndex: (theme) => theme.zIndex.drawer + 1,
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: "linear-gradient(180deg, rgba(8, 14, 10, 0.94) 0%, rgba(12, 22, 14, 0.88) 100%)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255, 193, 7, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.45)",
        color: "#f5f5f5",
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: SESSION_APPBAR_TOOLBAR_PX.xs, sm: SESSION_APPBAR_TOOLBAR_PX.sm },
          px: { xs: 1.25, sm: 1.5 },
          py: { xs: 0.5, sm: 0.65 },
          pr: toolbarTrailingReservePx
            ? { xs: `calc(1rem + ${toolbarTrailingReservePx}px)`, sm: `calc(1.25rem + ${toolbarTrailingReservePx}px)` }
            : undefined,
          gap: 1,
          maxWidth: 1200,
          width: "100%",
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: { xs: 28, sm: 30 },
              height: { xs: 28, sm: 30 },
              borderRadius: 1,
              bgcolor: "rgba(76,175,80,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SportsEsportsRoundedIcon sx={{ color: "#112116", fontSize: { xs: 18, sm: 19 } }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center" }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.25,
                fontSize: { xs: "1.05rem", sm: "1.12rem" },
              }}
            >
              Card Games
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 0.75 }, flexShrink: 0 }}>
          {onOpenLeaderboard ? (
            <Tooltip title="Leaderboard">
              <IconButton
                onClick={onOpenLeaderboard}
                aria-label="Leaderboard"
                size="medium"
                sx={{
                  color: "#f5f5f5",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                  p: 1,
                }}
              >
                <EmojiEventsRoundedIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {compact ? (
            <Tooltip title="Profile">
              <IconButton
                onClick={onOpenProfile}
                aria-label="Profile"
                size="medium"
                sx={{ color: "#f5f5f5", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" }, p: 1 }}
              >
                <PersonRoundedIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={displayName}>
              <IconButton
                onClick={onOpenProfile}
                aria-label="Profile"
                size="medium"
                sx={{ color: "#f5f5f5", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" }, p: 1 }}
              >
                <Avatar sx={{ width: 30, height: 30, fontSize: "0.85rem", bgcolor: "rgba(255,193,7,0.9)", color: "#111" }}>
                  {initial}
                </Avatar>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
